import type { HomeStateData } from '@workspace/api-backend'
import { PROFILE_COMPLETE_THRESHOLD } from '../../outreach/config/launch-checklist'

/**
 * Values the copy layer may interpolate.
 *
 * Every one is either a preformatted string or a plain count, so the templates
 * never do date maths or pluralisation inline. The server derives the day
 * counts, where it and the resolver share a clock.
 */
export interface CopyTokens {
  investorName: string
  daysStalled: string
  daysSinceTrialEnd: string
  daysUntilTrialEnd: string
  runwayDays: string
  queuedCount: string
  replyCount: string
  matchCount: string
  outcomePrice: string
  /** The completeness gate, so the sentence cannot drift from the constant. */
  unlockThreshold: string
}

export type TokenName = keyof CopyTokens

/** `1 day` / `3 days`, so no template has to carry a plural rule. */
export function pluralise(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`
}

/**
 * A duration in whole days, without ever claiming zero.
 *
 * Flooring is right for "3.7 days of runway" — promising a part-day you cannot
 * deliver is worse than under-promising — but it turns anything under 24 hours
 * into "0 days", which reads as already over. "less than a day" is the same
 * fact, stated in a way a founder can act on.
 */
export function daysRemaining(days: number): string {
  const whole = Math.floor(days)
  return whole < 1 ? 'less than a day' : pluralise(whole, 'day')
}

const TOKEN_PATTERN = /\{(\w+)\}/g

/**
 * Fill `{token}` placeholders.
 *
 * Throws on an unknown or missing token rather than rendering the braces. A
 * founder reading "stopped going out {daysStalled}" is worse than a loud
 * failure, and the states that hit this are the ones we can least afford to
 * look broken.
 */
export function interpolate(template: string, tokens: Partial<CopyTokens>): string {
  return template.replace(TOKEN_PATTERN, (_match, name: string) => {
    const value = tokens[name as TokenName]
    if (value === undefined) {
      throw new Error(`home copy: no value for {${name}} in "${template}"`)
    }
    return value
  })
}

/**
 * Build the token values for a resolved state.
 *
 * Day counts are nullable on the wire — status ERROR raises the stall card with
 * no push history at all — so each has a fallback phrase that reads naturally
 * in place of a number rather than leaving a gap.
 */
export function tokensFor(
  data: Pick<
    HomeStateData,
    | 'daysStalled'
    | 'daysSinceTrialEnd'
    | 'daysUntilTrialEnd'
    | 'queuedTotal'
    | 'matchCount'
    | 'replyCount'
    | 'runwayDays'
    | 'outcomeInvestorName'
    | 'outcomePriceCents'
  >
): CopyTokens {
  return {
    investorName: data.outcomeInvestorName ?? 'your investor',
    daysStalled:
      data.daysStalled != null ? `${pluralise(data.daysStalled, 'day')} ago` : 'a while ago',
    daysSinceTrialEnd:
      data.daysSinceTrialEnd != null
        ? `${pluralise(data.daysSinceTrialEnd, 'day')} ago`
        : 'recently',
    daysUntilTrialEnd:
      data.daysUntilTrialEnd != null ? daysRemaining(data.daysUntilTrialEnd) : 'soon',
    runwayDays: data.runwayDays != null ? daysRemaining(data.runwayDays) : 'a few days',
    queuedCount: String(data.queuedTotal),
    matchCount: String(data.matchCount),
    replyCount: pluralise(data.replyCount, 'reply', 'replies'),
    outcomePrice: `$${(data.outcomePriceCents / 100).toFixed(0)}`,
    unlockThreshold: `${PROFILE_COMPLETE_THRESHOLD}%`,
  }
}
